import React from 'react';
import HrnbInput from '../../../components/controls/HrnbInput';
import HrnbTextArea from '../../../components/controls/HrnbTextArea';
import HrnbSelect from '../../../components/controls/HrnbSelect';
import HrnbDateTimePicker from '../../../components/controls/HrnbDateTimePicker';
import HrnbButton from '../../../components/controls/HrnbButton';
import HrnbLoader from '../../../components/controls/HrnbLoader';
import HrnbModal from '../../../components/controls/HrnbModal';
import { ClearIcon, PlusIcon, DeleteIcon, SaveIcon } from '../../../icons';
import { ROUND_TYPE_OPTIONS, ROUND_STATUS_OPTIONS, VOTING_SYSTEM_OPTIONS, CRITERIA_PRESET_OPTIONS, LIMITATION_TYPE_OPTIONS } from '../constants';

/**
 * Модалка создания/редактирования раунда
 */
const RoundModal = ({
  isOpen,
  onClose,
  editingRoundId,
  roundForm,
  roundActionLoading,
  newRoundLimitation,
  onFormChange,
  onNewLimitationChange,
  onAddLimitation,
  onRemoveLimitation,
  onCreate,
  onUpdate,
}) => {
  const handleSubmit = editingRoundId ? onUpdate : onCreate;

  return (
    <HrnbModal
      open={isOpen}
      onClose={onClose}
      title={editingRoundId ? 'Редактирование раунда' : 'Создание раунда'}
      size="large"
    >
      <form onSubmit={handleSubmit} className="round-form">
        <div className="form-row">
          <HrnbInput
            label="Название *"
            value={roundForm.title}
            onChange={(e) => onFormChange('title', typeof e === 'string' ? e : e.target.value)}
            placeholder="Название раунда"
            required
          />
        </div>

        <div className="form-row">
          <HrnbTextArea
            label="Описание"
            value={roundForm.description}
            onChange={(e) =>
              onFormChange('description', typeof e === 'string' ? e : e.target.value)
            }
            placeholder="Описание раунда"
            rows={3}
          />
        </div>

        <div className="form-row">
          <HrnbInput
            label="Тема раунда"
            value={roundForm.theme}
            onChange={(e) =>
              onFormChange('theme', typeof e === 'string' ? e : e.target.value)
            }
            placeholder="Например: Свободная тема"
            maxLength={200}
          />
        </div>

        <div className="form-row">
          <HrnbTextArea
            label="Задание для раунда"
            value={roundForm.task}
            onChange={(e) =>
              onFormChange('task', typeof e === 'string' ? e : e.target.value)
            }
            placeholder="Например: Запиши трек на тему 'Лето', длительность 2-3 минуты"
            rows={3}
          />
        </div>

        <div className="form-row form-row-split">
          <HrnbSelect
            label="Тип *"
            value={roundForm.type}
            onChange={(val) => onFormChange('type', val)}
            options={ROUND_TYPE_OPTIONS}
            required
          />
          <HrnbInput
            label="Номер *"
            type="number"
            value={roundForm.number}
            onChange={(e) =>
              onFormChange('number', parseInt(typeof e === 'string' ? e : e.target.value) || 1)
            }
            min={1}
            required
          />
        </div>

        {editingRoundId && (
          <div className="form-row">
            <HrnbSelect
              label="Статус раунда"
              value={roundForm.status}
              onChange={(val) => onFormChange('status', val)}
              options={ROUND_STATUS_OPTIONS}
              required
            />
          </div>
        )}

        <div className="form-row form-row-split">
          <HrnbDateTimePicker
            label="Начало *"
            value={roundForm.startTime}
            onChange={(val) => onFormChange('startTime', val)}
            required
          />
          <HrnbDateTimePicker
            label="Окончание *"
            value={roundForm.endTime}
            onChange={(val) => onFormChange('endTime', val)}
            required
          />
        </div>

        <div className="form-row form-row-split">
          <HrnbDateTimePicker
            label="Дедлайн загрузки треков"
            value={roundForm.trackUploadDeadline}
            onChange={(val) => onFormChange('trackUploadDeadline', val)}
          />
          <HrnbDateTimePicker
            label="Окончание голосования"
            value={roundForm.votingEndTime}
            onChange={(val) => onFormChange('votingEndTime', val)}
          />
        </div>

        <div className="form-row">
          <label className="form-checkbox-label">
            <input
              type="checkbox"
              checked={roundForm.votingEnabled}
              onChange={(e) => {
                const isChecked = e.target.checked;
                onFormChange('votingEnabled', isChecked);
                // Если включаем голосование и время не указано, устанавливаем равным endTime
                if (isChecked && !roundForm.votingEndTime) {
                  onFormChange('votingEndTime', roundForm.endTime);
                }
              }}
            />{' '}
            Разрешить голосование
          </label>
        </div>

        <div className="form-row">
          <HrnbSelect
            label="Система голосования"
            value={roundForm.votingSystem || ''}
            onChange={(val) => onFormChange('votingSystem', val || '')}
            options={[
              { value: '', label: 'Не указано (по умолчанию)' },
              ...VOTING_SYSTEM_OPTIONS
            ]}
          />
        </div>

        {(roundForm.votingSystem === 'CRITERIA_BASED') && (
          <div className="form-row">
            <HrnbSelect
              label="Пресет критериев *"
              value={roundForm.criteriaPreset || ''}
              onChange={(val) => onFormChange('criteriaPreset', val || '')}
              options={[
                { value: '', label: 'Выберите пресет' },
                ...CRITERIA_PRESET_OPTIONS
              ]}
              required
            />
          </div>
        )}

        {/* Секция ограничений */}
        <div className="form-section">
          <h4 className="form-section-title">Ограничения раунда</h4>

          {/* Форма добавления ограничения */}
          <div className="limitation-form">
            <div className="form-row">
              <HrnbInput
                label="Название ограничения *"
                value={newRoundLimitation.name}
                onChange={(e) => onNewLimitationChange('name', typeof e === 'string' ? e : e.target.value)}
                placeholder="Например: Ограничение по времени"
              />
            </div>

            <div className="form-row">
              <HrnbTextArea
                label="Описание"
                value={newRoundLimitation.description}
                onChange={(e) =>
                  onNewLimitationChange('description', typeof e === 'string' ? e : e.target.value)
                }
                placeholder="Описание ограничения"
                rows={2}
              />
            </div>

            <div className="form-row">
              <HrnbSelect
                label="Тип ограничения *"
                value={newRoundLimitation.limitationType}
                onChange={(val) => onNewLimitationChange('limitationType', val)}
                options={LIMITATION_TYPE_OPTIONS}
                required
              />
            </div>

            {/* Секция: Ограничение по длительности трека */}
            {newRoundLimitation.limitationType === 'TRACK_DURATION' && (
              <div className="limitation-type-section">
                <h6 className="limitation-type-title">⏱️ Ограничение по длительности трека</h6>
                <div className="form-row form-row-split">
                  <HrnbInput
                    label="Мин. длительность (сек)"
                    type="number"
                    value={newRoundLimitation.minTrackDuration || ''}
                    onChange={(e) =>
                      onNewLimitationChange('minTrackDuration', parseInt(e.target.value) || null)
                    }
                    min={0}
                  />
                  <HrnbInput
                    label="Макс. длительность (сек)"
                    type="number"
                    value={newRoundLimitation.maxTrackDuration || ''}
                    onChange={(e) =>
                      onNewLimitationChange('maxTrackDuration', parseInt(e.target.value) || null)
                    }
                    min={0}
                  />
                </div>
              </div>
            )}

            {/* Секция: Ограничение по тексту */}
            {newRoundLimitation.limitationType === 'TEXT_LENGTH' && (
              <div className="limitation-type-section">
                <h6 className="limitation-type-title">📝 Ограничение по длине текста</h6>
                <div className="form-row form-row-split">
                  <HrnbInput
                    label="Мин. длина текста"
                    type="number"
                    value={newRoundLimitation.minTextLength || ''}
                    onChange={(e) =>
                      onNewLimitationChange('minTextLength', parseInt(e.target.value) || null)
                    }
                    min={0}
                  />
                  <HrnbInput
                    label="Макс. длина текста"
                    type="number"
                    value={newRoundLimitation.maxTextLength || ''}
                    onChange={(e) =>
                      onNewLimitationChange('maxTextLength', parseInt(e.target.value) || null)
                    }
                    min={0}
                  />
                </div>
              </div>
            )}

            {/* Секция: Ограничение по размеру файла */}
            {newRoundLimitation.limitationType === 'FILE_SIZE' && (
              <div className="limitation-type-section">
                <h6 className="limitation-type-title">📦 Ограничение по размеру файла</h6>
                <div className="form-row">
                  <HrnbInput
                    label="Макс. размер файла (МБ)"
                    type="number"
                    value={newRoundLimitation.maxFileSizeMb || ''}
                    onChange={(e) =>
                      onNewLimitationChange('maxFileSizeMb', parseInt(e.target.value) || null)
                    }
                    min={1}
                  />
                </div>
              </div>
            )}

            {/* Секция: Ограничение по формату */}
            {newRoundLimitation.limitationType === 'FILE_FORMAT' && (
              <div className="limitation-type-section">
                <h6 className="limitation-type-title">🎭 Ограничение по формату</h6>
                <div className="form-row">
                  <HrnbInput
                    label="Разрешённые форматы"
                    value={newRoundLimitation.allowedFormats}
                    onChange={(e) =>
                      onNewLimitationChange('allowedFormats', typeof e === 'string' ? e : e.target.value)
                    }
                    placeholder="mp3, wav, mp4"
                  />
                </div>
              </div>
            )}

            {/* Секция: Возрастное ограничение */}
            {newRoundLimitation.limitationType === 'AGE_RESTRICTION' && (
              <div className="limitation-type-section">
                <h6 className="limitation-type-title">🔞 Возрастное ограничение</h6>
                <div className="form-row">
                  <HrnbInput
                    label="Минимальный возраст"
                    type="number"
                    value={newRoundLimitation.minAge || ''}
                    onChange={(e) =>
                      onNewLimitationChange('minAge', parseInt(e.target.value) || null)
                    }
                    min={0}
                    max={99}
                    placeholder="18"
                  />
                </div>
              </div>
            )}

            <div className="form-row">
              <HrnbButton
                variant="secondary"
                type="button"
                onClick={onAddLimitation}
                disabled={!newRoundLimitation.name.trim()}
                icon={<PlusIcon />}
              >
                Добавить ограничение
              </HrnbButton>
            </div>
          </div>

          {/* Список добавленных ограничений */}
          {roundForm.limitations && roundForm.limitations.length > 0 && (
            <div className="limitations-list">
              <h5>Добавленные ограничения:</h5>
              <ul className="limitations-list-items">
                {roundForm.limitations.map((lim, index) => (
                  <li key={index} className="limitation-item">
                    <div className="limitation-item-info">
                      <strong>{lim.name}</strong>
                      {lim.limitationType && (
                        <span className="limitation-type-badge">{lim.limitationType}</span>
                      )}
                      {lim.description && <span className="limitation-desc">{lim.description}</span>}
                      {lim.maxTrackDuration && <span className="limitation-param">Макс. длительность: {lim.maxTrackDuration} сек</span>}
                      {lim.minTrackDuration && <span className="limitation-param">Мин. длительность: {lim.minTrackDuration} сек</span>}
                      {lim.maxTextLength && <span className="limitation-param">Макс. текст: {lim.maxTextLength} симв.</span>}
                      {lim.minTextLength && <span className="limitation-param">Мин. текст: {lim.minTextLength} симв.</span>}
                      {lim.maxFileSizeMb && <span className="limitation-param">Макс. размер: {lim.maxFileSizeMb} МБ</span>}
                      {lim.allowedFormats && <span className="limitation-param">Форматы: {lim.allowedFormats}</span>}
                      {lim.minAge && <span className="limitation-param">Мин. возраст: {lim.minAge}+</span>}
                    </div>
                    <HrnbButton
                      variant="outline"
                      size="sm"
                      type="button"
                      onClick={() => onRemoveLimitation(index)}
                      icon={<DeleteIcon />}
                    >
                      Удалить
                    </HrnbButton>
                  </li>
                ))}
              </ul>
            </div>
          )}
        </div>

        <div className="form-actions">
          <HrnbButton variant="outline" onClick={onClose} type="button" icon={<ClearIcon />}>
            Отмена
          </HrnbButton>
          <HrnbButton
            variant="primary"
            type="submit"
            disabled={roundActionLoading[editingRoundId || 'create']}
            icon={editingRoundId ? <SaveIcon /> : <PlusIcon />}
          >
            {roundActionLoading[editingRoundId || 'create'] ? (
              <HrnbLoader size="sm" />
            ) : (
              editingRoundId ? 'Сохранить' : 'Создать'
            )}
          </HrnbButton>
        </div>
      </form>
    </HrnbModal>
  );
};

export default RoundModal;
